import React, { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { Calendar, CreditCard, ExternalLink, ChevronLeft, ChevronRight, X } from 'lucide-react'

const CertificateCard = ({ certificate, index }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const images = Array.isArray(certificate.images) && certificate.images.length ? certificate.images : [certificate.image]
  const [current, setCurrent] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const next = () => setCurrent((i) => (i + 1) % images.length)
  const prev = () => setCurrent((i) => (i - 1 + images.length) % images.length)
  useEffect(() => {
    if (lightboxOpen || images.length <= 1) return
    const id = setInterval(next, 4000)
    return () => clearInterval(id)
  }, [lightboxOpen, images.length])

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 delay-${index * 100} ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div
        className="bg-white dark:bg-navy-deep rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-navy-pale dark:border-navy-base group hover:border-accent dark:hover:border-accent cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={() => setLightboxOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setLightboxOpen(true)
        }}
      >
        {/* Certificate Image */}
        <div className="relative h-48 overflow-hidden">
          <img
            src={images[current]}
            alt={certificate.title || 'Certificate'}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 cursor-pointer"
            onClick={() => setLightboxOpen(true)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous image"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white p-2 rounded-full hover:bg-black/60"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next image"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white p-2 rounded-full hover:bg-black/60"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}

          {/* Dots */}
          {images.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${i === current ? 'bg-white' : 'bg-white/60'}`}
                />
              ))}
            </div>
          )}
          
          {/* Skills Badge Overlay */}
          {Array.isArray(certificate.skills) && certificate.skills.length > 0 && (
            <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2">
              {certificate.skills.map((skill, idx) => (
                <span
                  key={idx}
                  className="text-xs bg-white/90 dark:bg-navy-dark/90 text-navy-dark dark:text-white px-2 py-1 rounded-full font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Certificate Details */}
        <div className="p-6">
          <h3 className="text-lg font-bold text-navy-dark dark:text-navy-lightest mb-2 group-hover:text-navy-medium transition-colors">
            {certificate.title}
          </h3>
          {Array.isArray(certificate.chips) && certificate.chips.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {certificate.chips.map((chip, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 text-xs bg-navy-pale dark:bg-navy-base text-navy-base dark:text-navy-soft rounded border border-navy-soft dark:border-navy-base"
                >
                  {typeof chip === 'string' ? chip : chip.label}
                </span>
              ))}
            </div>
          )}
          {(certificate.issuer || certificate.issueDate || certificate.credentialId) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {certificate.issuer && (
                <span className="px-2 py-1 text-xs bg-navy-pale dark:bg-navy-base text-navy-base dark:text-navy-soft rounded border border-navy-soft dark:border-navy-base">{certificate.issuer}</span>
              )}
              {certificate.issueDate && (
                <span className="px-2 py-1 text-xs bg-navy-pale dark:bg-navy-base text-navy-base dark:text-navy-soft rounded border border-navy-soft dark:border-navy-base flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-navy-medium" />
                  <span>Issued: {certificate.issueDate}</span>
                </span>
              )}
              {certificate.credentialId && (
                <span className="px-2 py-1 text-xs bg-navy-pale dark:bg-navy-base text-navy-base dark:text-navy-soft rounded border border-navy-soft dark:border-navy-base flex items-center gap-1">
                  <CreditCard className="w-3.5 h-3.5 text-navy-medium" />
                  <span className="font-mono">{certificate.credentialId}</span>
                </span>
              )}
            </div>
          )}

          {/* Verify Button */}
          {certificate.verifyUrl && (
            <a
              href={certificate.verifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-navy-medium hover:text-accent dark:text-accent dark:hover:text-navy-light transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <span>Verify Certificate</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close"
            className="absolute top-6 right-6 bg-white/20 text-white p-2 rounded-full hover:bg-white/30"
          >
            <X size={20} />
          </button>
          {images.length > 1 && (
            <button
              type="button"
              onClick={prev}
              aria-label="Previous"
              className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/20 text-white p-3 rounded-full hover:bg-white/30"
            >
              <ChevronLeft size={24} />
            </button>
          )}
          <img
            src={images[current]}
            alt={certificate.title || 'Certificate'}
            className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg"
          />
          {images.length > 1 && (
            <button
              type="button"
              onClick={next}
              aria-label="Next"
              className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/20 text-white p-3 rounded-full hover:bg-white/30"
            >
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default CertificateCard
